import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/8e9a6d69-4d01-4c64-94ed-2cc87f71b69b.png" alt="JEU SH.P.K. Logo" className="w-10 h-10 object-contain" />
            <span className={`text-lg font-semibold tracking-wide transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>JEU SH.P.K.</span>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>About</button>
            <button onClick={() => scrollToSection('products')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Products</button>
            <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-gray-200 hover:text-white'}`}>Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img 
          src="https://public.readdy.ai/ai/img_res/97ab03bb0d4fb75c04833342f85f88d1.jpg" 
          alt="Hero Background" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <div className="max-w-4xl">
            <h1 className="text-7xl font-light text-white leading-tight mb-8">
              <span className="font-bold">Modern</span> iOS<br />
              <span className="font-bold">Applications</span><br />
              <span className="font-bold">That</span> Matter
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-teal-400 to-cyan-400 mb-8"></div>
            <p className="text-xl text-gray-200 font-light max-w-2xl leading-relaxed">
              Specialized in VPN security and device optimization solutions for iOS ecosystem
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="inline-block px-5 py-2 bg-teal-50 rounded-full mb-12">
            <span className="text-xs uppercase tracking-wider text-teal-700 font-semibold">Our Expertise</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-6xl font-light text-gray-900 leading-tight mb-12">
                Crafting Premium<br />
                Applications for<br />
                Modern Mobile Life
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                JEU SH.P.K. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-10">
                <img 
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20iOS%20app%20development%20workspace%20showing%20MacBook%20with%20Xcode%20interface%2C%20iPhone%20devices%20displaying%20modern%20app%20interfaces%2C%20Swift%20code%20on%20screen%2C%20professional%20tech%20environment%20with%20soft%20natural%20lighting%2C%20organized%20desk%20setup%20with%20Apple%20products%2C%20contemporary%20office%20aesthetic%2C%20high%20quality%20product%20photography%20style&width=600&height=700&seq=about-img-001&orientation=portrait" 
                  alt="iOS Development" 
                  className="w-full h-96 object-cover rounded-2xl mb-8"
                />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-4xl font-bold text-gray-900">99.9%</div>
                    <div className="text-sm text-gray-600 mt-1">Uptime Guarantee</div>
                  </div>
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-full">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Solutions</h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* VPN Product */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 text-white relative overflow-hidden group hover:scale-105 transition-transform duration-300 cursor-pointer">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl mb-8">
                  <i className="ri-shield-line text-2xl text-white"></i>
                </div>
                <h3 className="text-3xl font-semibold mb-4">Enterprise-Grade VPN Protection</h3>
                <p className="text-gray-300 text-sm mb-8">Military-level encryption for your iOS devices</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-4 py-2 border border-white/30 rounded-full text-xs">AES-256</span>
                  <span className="px-4 py-2 border border-white/30 rounded-full text-xs">No Logs</span>
                  <span className="px-4 py-2 border border-white/30 rounded-full text-xs">Fast Servers</span>
                </div>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20abstract%20technology%20background%20with%20clean%20geometric%20patterns%2C%20soft%20gradient%20from%20white%20to%20light%20blue%2C%20minimalist%20digital%20interface%20elements%20suggesting%20storage%20and%20optimization%2C%20organized%20data%20visualization%2C%20professional%20tech%20aesthetic%20with%20subtle%20grid%20patterns%2C%20contemporary%20design%20perfect%20for%20mobile%20app%20showcase&width=500&height=600&seq=cleaner-bg-001&orientation=portrait" 
                alt="Cleaner Background" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
              <div className="relative z-10 p-10 h-full flex flex-col justify-between min-h-[400px]">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl self-start">
                  <i className="ri-brush-line text-white"></i>
                  <span className="text-white text-sm font-medium">Cleaner</span>
                </div>
                <div>
                  <h3 className="text-3xl font-semibold text-white mb-2">Intelligent Storage Optimization</h3>
                  <p className="text-gray-200 text-sm">Free up space automatically</p>
                </div>
              </div>
            </div>

            {/* Technology Focus */}
            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <h3 className="text-3xl font-light text-gray-900 mb-2">Swift & SwiftUI</h3>
              <p className="text-sm text-gray-600 mb-8">Native iOS development</p>
              <img 
                src="https://readdy.ai/api/search-image?query=Clean%20code%20editor%20interface%20showing%20Swift%20programming%20language%20syntax%20with%20colorful%20syntax%20highlighting%2C%20modern%20IDE%20workspace%2C%20SwiftUI%20code%20snippets%20visible%2C%20professional%20developer%20environment%2C%20minimalist%20design%20with%20focus%20on%20code%20quality%2C%20light%20theme%20interface%2C%20contemporary%20software%20development%20aesthetic&width=500&height=400&seq=tech-code-001&orientation=landscape" 
                alt="Swift Code" 
                className="w-full h-64 object-cover rounded-2xl border border-gray-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-40 overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=Panoramic%20abstract%20iOS%20interface%20elements%20with%20blur%20effect%2C%20modern%20mobile%20app%20UI%20components%20floating%20in%20space%2C%20sleek%20gradient%20backgrounds%20with%20teal%20and%20cyan%20accents%2C%20futuristic%20digital%20design%2C%20soft%20focus%20bokeh%20effect%2C%20contemporary%20tech%20aesthetic%2C%20wide%20cinematic%20composition%20perfect%20for%20banner%2C%20professional%20high-end%20look&width=1920&height=800&seq=cta-banner-001&orientation=landscape" 
          alt="CTA Background" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <h2 className="text-6xl font-bold text-white leading-tight">
              Ready to elevate<br />
              your iOS experience
            </h2>
            <div className="text-right">
              <p className="text-xl text-white font-light leading-relaxed">
                Discover how our applications<br />
                transform your mobile workflow
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Footer Section */}
      <footer id="contact" className="bg-gray-900 py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-between mb-16 pb-16 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/8e9a6d69-4d01-4c64-94ed-2cc87f71b69b.png" alt="JEU SH.P.K. Logo" className="w-8 h-8 object-contain" />
              <span className="text-xl font-semibold text-white">JEU SH.P.K.</span>
            </div>
            <p className="text-sm text-gray-400">Specialized iOS Development Since 2020</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
            <div>
              <h4 className="text-white font-semibold mb-6">Products</h4>
              <div className="space-y-3">
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">VPN Solution</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Cleaner App</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Security Suite</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Performance Tools</div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-6">Company</h4>
              <div className="space-y-3">
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">About Us</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Our Team</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Expertise</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Technology</div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-6">Resources</h4>
              <div className="space-y-3">
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Documentation</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Support</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Privacy Policy</div>
                <div className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">Terms of Service</div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-xl text-white font-light mb-2">© JEU SH.P.K. 2025</div>
              <div className="text-sm text-gray-400 mb-6">All rights reserved</div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20MacBook%20and%20iPhone%20devices%20on%20clean%20desk%2C%20contemporary%20tech%20company%20environment%2C%20Apple%20products%20arranged%20professionally%2C%20soft%20natural%20lighting%2C%20organized%20workspace%20aesthetic%2C%20high%20quality%20product%20photography%2C%20professional%20business%20setting%20with%20iOS%20development%20tools%20visible&width=600&height=450&seq=footer-workspace-001&orientation=landscape" 
                alt="Workspace" 
                className="w-full h-64 object-cover rounded-2xl"
              />
            </div>
            
            <div className="flex flex-col justify-center">
              <h3 className="text-4xl font-light text-white leading-snug">
                Building the future<br />
                of iOS applications<br />
                one line at a time
              </h3>
            </div>
            
            <div>
              <div className="mb-8">
                <h4 className="text-white font-semibold mb-4">Contact</h4>
                <div className="space-y-2 text-sm text-gray-400 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-line text-teal-400 mt-1"></i>
                    <div>
                      <strong className="text-gray-300">Address:</strong> Rr. Spiro Cipi, ish Magazinat NPA, ZK 8120, Nr 6/70
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-building-line text-teal-400"></i>
                    <div><strong className="text-gray-300">City:</strong> TIRANE | <strong className="text-gray-300">Zip:</strong> 1000</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-global-line text-teal-400"></i>
                    <div><strong className="text-gray-300">Country:</strong> Albania</div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <i className="ri-mail-line text-teal-400"></i>
                    <a href="mailto:info@jeushpk.com" className="hover:text-white transition-colors cursor-pointer">info@jeushpk.com</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-teal-400"></i>
                    <a href="tel:+19865292049" className="hover:text-white transition-colors cursor-pointer">+1 9865292049</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
